import { Form, Formik } from "formik";
import { keyBy } from "lodash";
import { computed } from "mobx";
import { inject, observer } from "mobx-react";
import React from "react";
import { FormattedMessage, InjectedIntlProps, injectIntl } from "react-intl";
import { RouteComponentProps, withRouter } from "react-router";
import { compose } from "recompose";

import { PrimaryBlueButton, SecondaryButton } from "nvent-web/components/Button";
import { DropdownField } from "nvent-web/components/form/DropdownField";
import * as logger from "nvent-web/services/logger";
import { NotificationsStore } from "nvent-web/stores/Notifications";
import { ProductsStore } from "nvent-web/stores/Products";
import { AddProductParams } from "nvent-web/types/AddProductParams";
import { CategoryType } from "nvent-web/types/CategoryType";
import { ProductSku } from "nvent-web/types/ProductSku";

import style from "./AddThermostatForm.module.scss";

interface FormValues {
  productSkuId: string;
}

const initialValues: FormValues = {
  productSkuId: "",
};

interface AddThermostatFormProps {
  onClose: () => void;
}

interface AddThermostatFormInnerProps
  extends AddThermostatFormProps,
    RouteComponentProps<AddProductParams>,
    InjectedIntlProps {
  products: ProductsStore;
  notifications: NotificationsStore;
}

class AddThermostatFormInner extends React.Component<AddThermostatFormInnerProps> {
  @computed
  get category() {
    const { availableProductCategories } = this.props.products;
    return availableProductCategories.find(({ type }) => type === CategoryType.thermostat);
  }

  @computed
  get products() {
    const categoryId = this.category ? this.category.id : null;

    if (!categoryId) {
      return [];
    }

    return this.props.products.availableProducts.filter(({ productCategoryId }) => productCategoryId === categoryId);
  }

  @computed
  get productSkus(): ProductSku[] {
    const productsById = keyBy(this.products, ({ id }) => id);

    return this.props.products.availableProductSkus.filter(({ productId }) => productId in productsById);
  }

  @computed
  get thermostatSkuOptions() {
    return this.productSkus.map(({ id, description }) => ({
      key: id.toString(),
      title: description,
    }));
  }

  componentDidMount() {
    this.props.products.loadProductsCatalog();
  }

  render() {
    const { intl, products } = this.props;
    const isSubmitting = products.isProductCreating;

    return (
      <Formik initialValues={initialValues} onSubmit={this.handleSubmit}>
        {({ dirty }) => (
          <Form noValidate={true}>
            <h3 className={style.title}>
              <FormattedMessage id="thermostat.add.title" />
            </h3>

            <p className={style.notice}>
              <FormattedMessage id="thermostat.add.notice" />
            </p>

            <DropdownField
              name="productSkuId"
              label={<FormattedMessage id="form.thermostatName" />}
              options={this.thermostatSkuOptions}
              placeholder={intl.formatMessage({ id: "form.thermostatName.selectPlaceholder" })}
              required
            />

            <div className={style.actions}>
              <SecondaryButton className={style.cancelBtn} onClick={this.handleCancel}>
                <FormattedMessage id="actions.cancel" />
              </SecondaryButton>
              <PrimaryBlueButton
                disabled={!dirty || isSubmitting}
                className={style.yesBtn}
                type="submit"
                loading={isSubmitting}
              >
                <FormattedMessage id="actions.add" />
              </PrimaryBlueButton>
            </div>
          </Form>
        )}
      </Formik>
    );
  }

  private handleCancel = () => {
    this.props.onClose();
  };

  private handleSubmit = async ({ productSkuId }: FormValues) => {
    const {
      products,
      match: {
        params: { projectId, roomId },
      },
      notifications,
    } = this.props;

    try {
      await products.createProductSku(Number(projectId), Number(roomId), { productSkuId: Number(productSkuId) });
      this.props.onClose();
    } catch (error) {
      logger.error(error);
      notifications.createError(<FormattedMessage id="error.productUnavailable" />);
    }
  };
}

const AddThermostatForm = compose<AddThermostatFormInnerProps, AddThermostatFormProps>(
  injectIntl,
  inject("products", "notifications"),
  withRouter,
  observer
)(AddThermostatFormInner);

export default AddThermostatForm;
